// Link-in-bio (page-bio.php): tag the links into the app so it knows which bio
// a visitor came from, report each tap, and run the blocks that need a hand —
// YouTube on tap, the gallery viewer, the testimonials slider.
//
// teamix.co.il/bio?src=tiktok → every app link gets utm_source=tiktok (no
// ?src → "bio"), utm_medium=link-in-bio. Links that already carry their own
// utm_source are left alone. The first-touch cookie written in header.php has
// already recorded the referrer by now; this adds the last hop.

type Tracker = {
  dataLayer?: unknown[];
  fbq?: (...args: unknown[]) => void;
};

const APP_HOST = /(^|\.)app\.teamix\.co\.il$/;

export function initBio(): void {
  const root = document.querySelector<HTMLElement>('[data-bio]');
  if (!root) return;

  const params = new URLSearchParams(location.search);
  const src = (params.get('src') || params.get('utm_source') || 'bio').toLowerCase().replace(/[^a-z0-9_-]/g, '').slice(0, 40) || 'bio';

  root.querySelectorAll<HTMLAnchorElement>('a[href]').forEach((a) => {
    let url: URL;
    try {
      url = new URL(a.href);
    } catch {
      return;
    }
    if (!APP_HOST.test(url.hostname) || url.searchParams.has('utm_source')) return;
    url.searchParams.set('utm_source', src);
    url.searchParams.set('utm_medium', 'link-in-bio');
    url.searchParams.set('utm_campaign', 'bio');
    a.href = url.toString();
  });

  const w = window as unknown as Tracker;
  root.addEventListener('click', (e) => {
    const a = (e.target as HTMLElement | null)?.closest<HTMLAnchorElement>('[data-bio-link]');
    if (!a) return;
    const link = a.dataset.bioLink ?? '';
    w.dataLayer?.push({ event: 'bio_click', bio_link: link, bio_src: src });
    w.fbq?.('trackCustom', 'BioClick', { link, src });
  });

  initVideos(root);
  initGallery(root);
  root.querySelectorAll<HTMLElement>('[data-bio-slider]').forEach(initSlider);
}

/** YouTube: a thumbnail until tapped, then the player (autoplaying, since they asked). */
function initVideos(root: HTMLElement): void {
  root.querySelectorAll<HTMLButtonElement>('[data-bio-video]').forEach((btn) => {
    btn.addEventListener('click', () => {
      const id = btn.dataset.bioVideo;
      if (!id || btn.querySelector('iframe')) return;
      const frame = document.createElement('iframe');
      frame.src = `https://www.youtube-nocookie.com/embed/${encodeURIComponent(id)}?autoplay=1&rel=0&playsinline=1`;
      frame.title = btn.getAttribute('aria-label') ?? 'YouTube';
      frame.allow = 'accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture';
      frame.allowFullscreen = true;
      btn.replaceChildren(frame);
      btn.disabled = true;
    });
  });
}

/** Gallery: tap a photo for a full-screen viewer; arrows, swipe and keys to move. */
function initGallery(root: HTMLElement): void {
  const shots = Array.from(root.querySelectorAll<HTMLButtonElement>('[data-bio-shot]'));
  if (!shots.length) return;

  const dlg = document.createElement('dialog');
  dlg.className = 'bio-lightbox';
  dlg.innerHTML = `
    <img class="bio-lightbox__img" alt="" />
    <button type="button" class="bio-lightbox__btn bio-lightbox__btn--close" aria-label="סגירה">✕</button>
    <button type="button" class="bio-lightbox__btn bio-lightbox__btn--prev" aria-label="הקודמת">›</button>
    <button type="button" class="bio-lightbox__btn bio-lightbox__btn--next" aria-label="הבאה">‹</button>`;
  document.body.appendChild(dlg);
  const img = dlg.querySelector<HTMLImageElement>('img')!;
  let at = 0;

  const show = (i: number) => {
    at = (i + shots.length) % shots.length;
    img.src = shots[at].dataset.bioShot ?? '';
    img.alt = shots[at].querySelector('img')?.alt ?? '';
  };
  shots.forEach((s, i) =>
    s.addEventListener('click', () => {
      show(i);
      dlg.showModal();
    }),
  );
  dlg.querySelector('.bio-lightbox__btn--close')!.addEventListener('click', () => dlg.close());
  dlg.querySelector('.bio-lightbox__btn--prev')!.addEventListener('click', () => show(at - 1));
  dlg.querySelector('.bio-lightbox__btn--next')!.addEventListener('click', () => show(at + 1));
  // A tap on the dark area closes it.
  dlg.addEventListener('click', (e) => {
    if (e.target === dlg) dlg.close();
  });
  // RTL: the next photo is to the left.
  dlg.addEventListener('keydown', (e) => {
    if (e.key === 'ArrowLeft') show(at + 1);
    if (e.key === 'ArrowRight') show(at - 1);
  });
  let x0: number | null = null;
  dlg.addEventListener('touchstart', (e) => (x0 = e.touches[0].clientX), { passive: true });
  dlg.addEventListener('touchend', (e) => {
    if (x0 === null) return;
    const dx = e.changedTouches[0].clientX - x0;
    if (Math.abs(dx) > 40) show(dx > 0 ? at + 1 : at - 1);
    x0 = null;
  });
}

/**
 * Testimonials slider: native scroll-snap (so a swipe is a swipe), plus
 * arrows, dots and a gentle auto-advance that stops for good the moment the
 * visitor touches it — and only runs while it's on screen.
 */
function initSlider(section: HTMLElement): void {
  const track = section.querySelector<HTMLElement>('[data-bio-track]');
  const dotsWrap = section.querySelector<HTMLElement>('[data-bio-dots]');
  if (!track) return;
  const slides = Array.from(track.children) as HTMLElement[];
  if (slides.length < 2) return;

  let current = 0;
  const dots = slides.map((_, i) => {
    const b = document.createElement('button');
    b.type = 'button';
    b.setAttribute('aria-label', `המלצה ${i + 1}`);
    b.addEventListener('click', () => {
      stop();
      go(i);
    });
    dotsWrap?.appendChild(b);
    return b;
  });
  const mark = (i: number) => {
    current = i;
    dots.forEach((d, j) => d.setAttribute('aria-current', String(j === i)));
  };

  // Physical offsets, so it behaves the same in RTL whatever the browser's scrollLeft convention.
  const go = (i: number) => {
    const target = slides[(i + slides.length) % slides.length];
    const t = track.getBoundingClientRect();
    const r = target.getBoundingClientRect();
    track.scrollBy({ left: r.left - t.left - (t.width - r.width) / 2, behavior: 'smooth' });
  };

  const io = new IntersectionObserver(
    (entries) => {
      entries.forEach((en) => {
        if (en.isIntersecting) mark(slides.indexOf(en.target as HTMLElement));
      });
    },
    { root: track, threshold: 0.6 },
  );
  slides.forEach((s) => io.observe(s));
  mark(0);

  section.querySelector('[data-bio-prev]')?.addEventListener('click', () => {
    stop();
    go(current - 1);
  });
  section.querySelector('[data-bio-next]')?.addEventListener('click', () => {
    stop();
    go(current + 1);
  });

  let timer: number | undefined;
  let stopped = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  const stop = () => {
    stopped = true;
    window.clearInterval(timer);
  };
  ['pointerdown', 'wheel', 'focusin'].forEach((ev) => track.addEventListener(ev, stop, { passive: true }));
  new IntersectionObserver(([en]) => {
    window.clearInterval(timer);
    if (en.isIntersecting && !stopped) timer = window.setInterval(() => go(current + 1), 5000);
  }).observe(section);
}
